import type { FlowStepType } from '@twisto/flow-views'

import { routes } from 'flow/routes'

type StepRouteKey = keyof typeof routes.step

type GetFlowStepRouteArgs = {
  step?: FlowStepType
  sessionId: string
}

/**
 * Route for every step the flow API can return.
 *
 * Steps without a dedicated page (Cash, Installments, bank account connection)
 * stay on the session root: the `[[...slug]]` page renders whichever step is
 * current anyway.
 *
 * Typed as a full `Record` on purpose: a new step in the GraphQL schema does
 * not compile until it is mapped here.
 */
export const STEP_ROUTE_KEYS: Record<FlowStepType, StepRouteKey> = {
  AccountExistsStep: 'accountExists',
  AccountExistsWithoutPasswordStep: 'accountExistsWithoutPassword',
  AddNewBankAccountStep: 'root',
  AdditionalInfoIntroStep: 'additionalInfoIntro',
  BankAccountConnectionErrorStep: 'root',
  BankAccountConnectionPartialStep: 'root',
  BankAccountConnectionProcessingStep: 'root',
  BankAccountConnectionTooLongStep: 'root',
  BankConnectionIntroStep: 'bankConnectionIntro',
  BankConnectionSuccessStep: 'bankConnectionSuccess',
  BankIdAuthenticationStep: 'bankId',
  BankIdConfirmationStep: 'bankId',
  BankIdConsentStep: 'bankId',
  BankIdErrorStep: 'bankId',
  BankIdIntroStep: 'bankId',
  BankIdProcessingStep: 'bankId',
  BaseRedirectStep: 'redirect',
  CZCashReviewRequestStep: 'root',
  CZIncomeVerificationBannerStep: 'root',
  CZSelectCashAmountStep: 'root',
  CashAgreementsStep: 'root',
  CashBankAccountEligibilityStep: 'root',
  CashBankAccountNameConsentStep: 'root',
  CashBankAccountsListStep: 'root',
  CashCompletedStep: 'root',
  CashDocumentsStep: 'root',
  CashInstallmentsOfferStep: 'root',
  CashReviewRequestStep: 'root',
  ConsentCollectionCompletedStep: 'consentCollectionCompleted',
  ConsentCollectionIntroStep: 'consentCollection',
  CountryOfBirthStep: 'countryOfBirth',
  CrossCheckWarningStep: 'crossCheckWarning',
  CzBasicAccountAgreementsStep: 'czBasicAccountAgreements',
  EducationTypeStep: 'educationType',
  EmailPrivacyPolicyStep: 'emailPrivacyPolicy',
  EmploymentTypeStep: 'employmentType',
  ExpensesStep: 'expenses',
  FamilyChildrenStep: 'familyChildren',
  FamilyStatusStep: 'familyStatus',
  GeneralErrorStep: 'generalError',
  HousingTypeStep: 'housingType',
  HousingExpenditureStep: 'housingExpenditureStep',
  OtherExpenditureStep: 'otherExpenditureStep',
  StartingOfferStep: 'startingOffer',
  FinalOfferStep: 'finalOffer',
  FinalSummaryStep: 'finalSummary',
  FinalOfferRevolvingOnlyStep: 'finalOfferRevolvingOnly',
  StartingOfferLowerIncomeStep: 'startingOfferLowerIncomeStep',
  Psd2MandatoryPromptStep: 'psd2MandatoryPromptStep',
  KontomatikRedirectStep: 'kontomatikRedirectStep',
  KontomatikRetryPromptStep: 'kontomatikRetryPromptStep',
  TransientErrorStep: 'transientErrorStep',
  IdentityVerificationIntroStep: 'identityVerificationIntro',
  IdentityVerificationManualIdInfoStep: 'identityVerificationManualIdInfo',
  IdentityVerificationRetryStep: 'identityVerificationRetry',
  IncomeStep: 'income',
  IncomeTypeStep: 'incomeType',
  IncomeVerificationBankAccountConnectionErrorStep:
    'incomeVerificationBankAccountConnectionError',
  IncomeVerificationBankAccountConnectionTooLongStep:
    'incomeVerificationBankAccountConnectionTooLong',
  IncomeVerificationBankAccountConnectionWarningStep:
    'incomeVerificationBankAccountConnectionWarning',
  IncomeVerificationCompletedStep: 'incomeVerificationCompleted',
  IncomeVerificationIntroStep: 'incomeVerificationIntro',
  IncomeVerifyIntroStep: 'incomeVerifyIntroStep',
  InstallmentsAgreementsStep: 'root',
  InstallmentsDisclaimerStep: 'root',
  InternationalAddressStep: 'internationalAddress',
  KontomatikErrorStep: 'kontomatikErrorStep',
  KontomatikLowIncomeStep: 'kontomatikLowIncomeStep',
  KontomatikNoIncomeStep: 'kontomatikNoIncomeStep',
  KontomatikPendingResultStep: 'kontomatikPendingResultStep',
  KontomatikSuccessStep: 'kontomatikSuccessStep',
  KontomatikTimeoutStep: 'kontomatikTimeoutStep',
  LoanRepaymentsStep: 'loanRepayment',
  MObywatelConfirmationStep: 'mObywatel',
  MObywatelErrorStep: 'mObywatel',
  MObywatelIntroStep: 'mObywatel',
  MObywatelProcessingStep: 'mObywatel',
  MObywatelQRProcessingStep: 'mObywatel',
  NameStep: 'name',
  OnboardingCompletedStep: 'onboardingCompleted',
  OnboardingErrorStep: 'onboardingError',
  OnboardingPlanPromoStep: 'onboardingPlanPromo',
  OnboardingRejectedStep: 'onboardingRejected',
  OtpVerificationEmailStep: 'otpVerificationEmail',
  OtpVerificationSmsStep: 'otpVerificationSms',
  PaidDividedPaymentsCompletedStep: 'root',
  PaidPayIn3CompletedStep: 'root',
  PasswordStep: 'password',
  PayInNCompletedStep: 'payInNCompleted',
  PayInNDeclinedStep: 'payInNDeclined',
  PayInNDocumentsConfirmationStep: 'payInNDocumentsConfirmation',
  PayInNDocumentsStep: 'payInNDocuments',
  PayInNDownpaymentStep: 'payInNDownpayment',
  PayInNEmailCheckStep: 'payInNEmailCheck',
  PayInNInstallmentCalculatorConfirmationStep:
    'payInNInstallmentCalculatorConfirmation',
  PayInNOtpVerificationEmailStep: 'root',
  PayInNOtpVerificationSmsStep: 'payInNOtpVerificationSms',
  PayInNPlanChoiceStep: 'payInNPlanChoice',
  PayInNPreSelectedPlansStep: 'payInNPreSelectedPlans',
  PayInNRejectedStep: 'payInNRejected',
  PayInNScoringStep: 'payInNScoring',
  PayInNSecurityCodeSetupStep: 'payInNSecurityCodeSetup',
  PersonalIdStep: 'personalId',
  PhoneNumberStep: 'phoneNumber',
  PlBasicAccountAgreementsStep: 'plBasicAccountAgreements',
  PremiumAccountAgreementsStep: 'premiumAccountAgreements',
  ProcessingStep: 'processing',
  ReviewIncomeStep: 'reviewIncome',
  RiskApprovedStep: 'riskApproved',
  RiskCheckProcessingStep: 'riskCheckProcessing',
  RiskManualReviewStep: 'riskManualReviewStep',
  SaltEdgeRedirectStep: 'redirect',
  SecurityCodeStep: 'payInNSecurityCode',
  SelectCashAmountStep: 'root',
  SingleInstallmentsOfferStep: 'root',
  VeriffRedirectStep: 'redirect',
  WebViewRedirectStep: 'redirect',
}

export const getFlowStepRoute = ({ step, sessionId }: GetFlowStepRouteArgs) =>
  routes.step[step ? STEP_ROUTE_KEYS[step] : 'root'].getPath({ sessionId })
